import {
  Dispatch,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import dayjs from "dayjs";
import { getListCustomer, updateCustomerStatus } from "../../services";
import {
  Customer,
  CustomerBilltype,
  CustomerEtaxChannel,
  CustomerResponse,
  CustomerStatus,
  CustomerSortBy,
  SortOrder,
} from "../../types/customer.type";
import { useNavigate } from "react-router-dom";
import { CUSTOMER_STATUS_DISPLAY, customerStatusDisplay } from "./data";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { featureAccess } from "../../permissions/access";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Skeleton from "@mui/material/Skeleton";
import EditIcon from "@mui/icons-material/Edit";
import CustomerPagination from "./Pagination";

interface CustomerTableProps {
  searchName: string;
  customerCode: string;
  status: string;
  billType: string;
  etaxChannel: string;
  routeCode: string;
  branch: string;
  sortBy: string;
  setSortBy: Dispatch<SetStateAction<string>>;
  sortOrder: string;
  setSortOrder: Dispatch<SetStateAction<string>>;
  triggerFetch: number;
  /** Absent for a reader who may not edit shops, which drops the การดำเนินการ column. */
  onOpenEditDrawer?: (customerId: string) => void;
}

/** Every column but การดำเนินการ. */
const DATA_COLUMN_COUNT = 8;

const renderSkeletonRows = (columnCount: number) => {
  return Array.from({ length: 10 }).map((_, index) => (
    <TableRow key={index}>
      {Array.from({ length: columnCount }).map((__, colIndex) => (
        <TableCell key={colIndex}>
          <Skeleton variant="text" />
        </TableCell>
      ))}
    </TableRow>
  ));
};

const LIMIT = 10;

// Drop "all"/empty sentinel filter values before sending to the API.
const cleanParam = (value: string) =>
  value && value !== "all" ? value : undefined;

export default function CustomerTable({
  searchName,
  customerCode,
  status,
  billType,
  etaxChannel,
  routeCode,
  branch,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  triggerFetch,
  onOpenEditDrawer,
}: CustomerTableProps) {
  const navigate = useNavigate();
  const role = useUserRole();
  // Closing a shop is an approver decision, not the GM's alone: head office and the
  // back office asked for the same switch. The server gates the call identically.
  const canEditStatus = featureAccess.customerStatusEdit(role);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [updatingId, setUpdatingId] = useState<string>("");
  const columnCount = onOpenEditDrawer
    ? DATA_COLUMN_COUNT + 1
    : DATA_COLUMN_COUNT;

  const getCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getListCustomer({
        page,
        limit,
        name: searchName || undefined,
        customer_code: customerCode || undefined,
        route_code: cleanParam(routeCode),
        branch_uuid: cleanParam(branch),
        status: cleanParam(status) as CustomerStatus | undefined,
        bill_type: cleanParam(billType) as CustomerBilltype | undefined,
        etax_channel: cleanParam(etaxChannel) as
          | CustomerEtaxChannel
          | undefined,
        sort_by: (sortBy || undefined) as CustomerSortBy | undefined,
        sort_order: (sortOrder || undefined) as SortOrder | undefined,
      });

      const formatted: Customer[] = data.rows.map(
        (customer: CustomerResponse, index: number) => ({
          id: customer.uuid,
          no: (page - 1) * limit + index + 1,
          customerCode: customer.customer_code,
          customerName: customer.name,
          billType: customer.billtype || "-",
          branch: customer.inv_branchname || customer.inv_name || "-",
          contactName: customer.contact_name,
          status: customer.status,
          isNew: customer.created_at
            ? dayjs(customer.created_at).isSame(dayjs(), "day")
            : false,
          createdAt: customer.created_at ?? undefined,
        }),
      );

      setCustomers(formatted);
      setTotalCount(data.count);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    searchName,
    customerCode,
    routeCode,
    branch,
    status,
    billType,
    etaxChannel,
    sortBy,
    sortOrder,
  ]);

  useEffect(() => {
    getCustomers();
  }, [triggerFetch, getCustomers]);

  // Reset to first page whenever a filter/sort/search value changes.
  useEffect(() => {
    setPage(1);
  }, [
    searchName,
    customerCode,
    routeCode,
    branch,
    status,
    billType,
    etaxChannel,
    sortBy,
    sortOrder,
  ]);

  const handleChangeStatus = async (
    customer: Customer,
    nextStatus: CustomerStatus,
  ) => {
    if (updatingId || nextStatus === customer.status) return;
    setUpdatingId(customer.id);
    try {
      await updateCustomerStatus(customer.id, nextStatus);
      await getCustomers();
    } finally {
      setUpdatingId("");
    }
  };

  // The row itself opens the shop's page, so the edit click must not reach it.
  const handleOpenEditDrawer =
    (customerId: string) => (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onOpenEditDrawer?.(customerId);
    };

  const handleSort = (field: CustomerSortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder(field === "created" ? "desc" : "asc");
    }
  };

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead
            sx={{
              bgcolor: (theme) => theme.palette.background.default,
            }}
          >
            <TableRow>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ลำดับ
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
                sortDirection={
                  sortBy === "customer_code"
                    ? (sortOrder as "asc" | "desc")
                    : false
                }
              >
                <TableSortLabel
                  active={sortBy === "customer_code"}
                  direction={
                    sortBy === "customer_code"
                      ? (sortOrder as "asc" | "desc")
                      : "asc"
                  }
                  onClick={() => handleSort("customer_code")}
                >
                  รหัสลูกค้า
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ชื่อลูกค้า
              </TableCell>

              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                สาขา
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
              >
                ชื่อผู้ติดต่อ
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
                width={150}
              >
                ประเภทบิล
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
                sortDirection={
                  sortBy === "created" ? (sortOrder as "asc" | "desc") : false
                }
              >
                <TableSortLabel
                  active={sortBy === "created"}
                  direction={
                    sortBy === "created" ? (sortOrder as "asc" | "desc") : "asc"
                  }
                  onClick={() => handleSort("created")}
                >
                  วันที่สร้าง
                </TableSortLabel>
              </TableCell>
              <TableCell
                sx={{
                  borderRight: (theme) =>
                    `1px solid ${theme.palette.draft.main}`,
                }}
                width={140}
              >
                สถานะ
              </TableCell>
              {onOpenEditDrawer && <TableCell>การดำเนินการ</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && renderSkeletonRows(columnCount)}
            {!loading && customers.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลลูกค้า
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              customers.length > 0 &&
              customers.map((customer, index) => (
                <TableRow
                  key={customer.id}
                  sx={{
                    bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                    "& td, & th": { padding: "8px" },
                    cursor: "pointer",
                  }}
                  onClick={() => navigate(`/customer/details/${customer.id}`)}
                >
                  <TableCell>{customer.no}</TableCell>
                  <TableCell>{customer.customerCode}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <span>{customer.customerName}</span>
                      {customer.isNew && (
                        <Chip
                          label="ใหม่"
                          color="warning"
                          size="small"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{customer.branch}</TableCell>
                  <TableCell>{customer.contactName}</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={customer.billType}
                      color={
                        customer.billType === "cash" ? "primary" : "success"
                      }
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>
                    {customer.createdAt
                      ? dayjs(customer.createdAt).format("DD/MM/YY")
                      : "-"}
                  </TableCell>
                  <TableCell align="center">
                    {canEditStatus ? (
                      <Select
                        size="small"
                        value={customer.status}
                        disabled={updatingId === customer.id}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event: SelectChangeEvent<CustomerStatus>) => {
                          event.stopPropagation();
                          handleChangeStatus(
                            customer,
                            event.target.value as CustomerStatus,
                          );
                        }}
                        sx={{ minWidth: 130, fontSize: "0.8rem" }}
                      >
                        {(
                          Object.keys(CUSTOMER_STATUS_DISPLAY) as CustomerStatus[]
                        ).map((status) => (
                          <MenuItem key={status} value={status}>
                            {CUSTOMER_STATUS_DISPLAY[status].label}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Chip
                        label={customerStatusDisplay(customer.status).label}
                        color={customerStatusDisplay(customer.status).color}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  {onOpenEditDrawer && (
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton
                          size="small"
                          color="primary"
                          sx={{ p: 1.5 }}
                          onClick={handleOpenEditDrawer(customer.id)}
                        >
                          <EditIcon fontSize="small" />
                          <Typography variant="subtitle2">แก้ไข</Typography>
                        </IconButton>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomerPagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
